/**
 * InvitationInput Component
 * Email input with role selector and add button
 * Shared between onboarding InvitationFormSection and BatchInvitationModal
 */

import type { ChangeEvent, KeyboardEvent } from 'react';
import { memo, useState, useCallback } from 'react';
import { TextInput } from '@mantine/core';
import { IconMail, IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { RoleSelector } from '@/components/common';
import {
  INVITATION_BATCH_MAX_SIZE,
  INVITABLE_ROLES,
} from '@/constants/workspace';
import { ADD_BUTTON_ICON_SIZE, ICON_SIZE_SM } from '@/constants/ui';
import { emailsMatch, isValidEmail } from '@/utils/helpers';
import type { InvitableRole, WorkspaceRole } from '@/types/workspace';
import type { InvitationInputProps } from '../types';
import styles from './InvitationInput.module.css';

export const InvitationInput = memo(function InvitationInput({
  onAdd,
  pendingInvitations,
  isSubmitting = false,
  isAtLimit = false,
  showMailIcon = false,
}: InvitationInputProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InvitableRole>('Editor');
  const [error, setError] = useState<string | null>(null);

  const validateEmail = useCallback(
    (emailValue: string): boolean => {
      if (!emailValue.trim()) {
        setError(t('workspace.invitations.validation.emailRequired'));
        return false;
      }
      if (!isValidEmail(emailValue.trim())) {
        setError(t('workspace.invitations.validation.emailInvalid'));
        return false;
      }
      if (
        pendingInvitations.some((invitation) =>
          emailsMatch(invitation.email, emailValue)
        )
      ) {
        setError(t('workspace.invitations.validation.emailDuplicate'));
        return false;
      }
      setError(null);
      return true;
    },
    [pendingInvitations, t]
  );

  const handleAdd = useCallback(() => {
    if (!validateEmail(email)) {
      return;
    }

    onAdd({
      email: email.trim().toLowerCase(),
      role,
    });

    setEmail('');
    setError(null);
  }, [email, role, validateEmail, onAdd]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleAdd();
      }
    },
    [handleAdd]
  );

  const handleEmailChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setEmail(event.target.value);
      if (error) {
        setError(null);
      }
    },
    [error]
  );

  const handleRoleChange = useCallback((newRole: WorkspaceRole) => {
    setRole(newRole as InvitableRole);
  }, []);

  const computedIsAtLimit =
    isAtLimit || pendingInvitations.length >= INVITATION_BATCH_MAX_SIZE;
  const isAddDisabled = computedIsAtLimit || isSubmitting || !email.trim();

  return (
    <div className={styles.inputRow}>
      <TextInput
        placeholder={t('workspace.invitations.emailPlaceholder')}
        value={email}
        onChange={handleEmailChange}
        onKeyDown={handleKeyDown}
        error={error}
        disabled={computedIsAtLimit || isSubmitting}
        className={styles.emailInput}
        size="sm"
        leftSection={
          showMailIcon ? <IconMail size={ICON_SIZE_SM} /> : undefined
        }
      />
      <RoleSelector
        value={role}
        onChange={handleRoleChange}
        availableRoles={INVITABLE_ROLES}
        size="md"
        disabled={isSubmitting}
      />
      <button
        type="button"
        className={`${styles.addButton} ${isAddDisabled ? styles.addButtonDisabled : ''}`}
        onClick={handleAdd}
        disabled={isAddDisabled}
        aria-label={t('workspace.form.addInvitation')}
      >
        <IconPlus size={ADD_BUTTON_ICON_SIZE} />
      </button>
    </div>
  );
});
